import { useReduxState } from 'hooks';
import { getMessageWithPlaceholders } from 'shared-locales';
import { Dropdown, DropdownButton } from 'components/common/dropdown';

import type { BlockLikert } from 'shared-types';

interface Props {
  blockId: number;
  currentColormap: BlockLikert['colormap'];
  currentNumberOfOptions: number;
  isPreview?: boolean;
  maxNumberOptions?: number;
  minNumberOptions?: number;
  onColormapSelection: (colormap: BlockLikert['colormap']) => void;
  onOptionsSelection: (numberOfOptions: number) => void;
}

const EditorBlockLikertOptionsConfig = ({
  blockId,
  currentColormap,
  currentNumberOfOptions = 5,
  isPreview = false,
  maxNumberOptions = 10,
  minNumberOptions = 2,
  onColormapSelection,
  onOptionsSelection
}: Props) => {
  const { messages, theme } = useReduxState();

  const options = Array.from(
    { length: maxNumberOptions - minNumberOptions + 1 },
    (_, i) => {
      const value = i + minNumberOptions;
      return {
        value,
        label: getMessageWithPlaceholders({
          message: messages.commonNumberOfOptions,
          placeholders: { numberOfOptions: value }
        })
      };
    }
  );

  const colormaps: Record<BlockLikert['colormap'], string> = {
    discrete: messages.editorBlockLikertColormapDiscrete,
    gradient: messages.editorBlockLikertColormapGradient,
    divergent: messages.editorBlockLikertColormapDivergent
  };

  return (
    <>
      <div className="options-dropdown">
        <div className="text">{messages.blocksMultipleChoiceOptions}</div>
        <Dropdown
          align="left"
          arrowStyle={{ marginLeft: 2 }}
          buttonStyle={{
            backgroundColor: 'transparent',
            color: theme.textColorLighter,
            fontSize: '1.5rem',
            fontWeight: 600,
            padding: 0,
            position: 'relative',
            left: 5
          }}
          disabled={isPreview}
          showArrow={true}
          styleContentButtons={false}
          title={getMessageWithPlaceholders({
            message: messages.commonNumberOfOptions,
            placeholders: { numberOfOptions: currentNumberOfOptions }
          })}
          titleStyle={{ marginRight: 2 }}
          underlineOnHover={true}
        >
          {options.map((option) => (
            <DropdownButton
              key={`${blockId}-likert-answer-${option.value}`}
              isSelected={currentNumberOfOptions === option.value}
              onClick={() => onOptionsSelection(option.value)}
              style={{ paddingRight: 30 }}
              text={option.label}
            />
          ))}
        </Dropdown>
      </div>

      <div className="colormap">
        <div className="text">{`${messages.editorBlockLikertColormap}:`}</div>
        <Dropdown
          align="left"
          arrowStyle={{ marginLeft: 2 }}
          buttonStyle={{
            backgroundColor: 'transparent',
            color: theme.textColorLighter,
            fontSize: '1.4rem',
            fontWeight: 400,
            marginLeft: 5,
            padding: 0,
            position: 'relative'
          }}
          disabled={isPreview}
          dropdownStyle={{ width: 200 }}
          showArrow={true}
          styleContentButtons={false}
          title={colormaps[currentColormap]}
          titleStyle={{ marginRight: 2 }}
          underlineOnHover={true}
        >
          {Object.keys(colormaps).map((option: BlockLikert['colormap']) => (
            <div key={`${blockId}-likert-colormap-${option}`}>
              <DropdownButton
                isSelected={currentColormap === option}
                onClick={() => onColormapSelection(option)}
                text={colormaps[option]}
              />
            </div>
          ))}
        </Dropdown>
      </div>

      <style jsx>{`
        .options-dropdown {
          display: inline-block;
        }
        .text {
          display: inline-block;
        }
        .colormap {
          font-size: 1.4rem;
          font-weight: 400;
          margin-top: 5px;
        }
      `}</style>
    </>
  );
};

export default EditorBlockLikertOptionsConfig;
